/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import PropTypes from 'prop-types';
import Container from '../../../components/Container';
import api from '../../../utils/API';
import { API_URLS } from '../../../constants/api_urls';
import './style.scss';

const OnboardingFileViewer = ({ open, onClose, mimeType, fileName, fileData }) => {
  const {path: filePath, id: onboardingId} = fileData;
  const [blobUrl, setBlobUrl] = useState(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [error, setError] = useState(null);

  const isPdfFile = useMemo(() => {
    if (mimeType) {
      return mimeType === 'application/pdf';
    }
    if (fileName) {
      const extension = fileName.toLowerCase().split('.').pop();
      return extension === 'pdf';
    }
    return false;
  }, [mimeType, fileName]);

  const imageMimeType = useMemo(() => {
    if (mimeType && mimeType.startsWith('image/')) {
      return mimeType;
    }
    if (fileName) {
      const extension = fileName.toLowerCase().split('.').pop();
      const mimeTypes = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
      };
      return mimeTypes[extension] || 'image/jpeg';
    }
    return 'image/jpeg';
  }, [mimeType, fileName]);

  useEffect(() => {
    if (!open || !onboardingId || !filePath) {
      setBlobUrl(null);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl = null;

    const loadFile = async () => {
    try {
      setError(null);
      const response = await api.get(
        API_URLS.GET_ONBOARDING_ATTACHMENT_V3(filePath, onboardingId),
        {
          responseType: 'blob',
          signal: controller.signal,
        }
      );
      const blob = response.data; // octet-stream уже собран браузером
      objectUrl = URL.createObjectURL(blob);
      setBlobUrl(objectUrl);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      setError('Failed to load file');
      setBlobUrl(null);
    }
  };
  loadFile();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, onboardingId, filePath]);

  const handleClose = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    setDataUrl(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
      destroyOnClose
      width={isPdfFile ? '80%' : '650px'}
      className="modal-content-paper onboarding-file-viewer"
      open={open}
      onCancel={handleClose}
      footer={null}
      zIndex={1600}
    >
      <Container widthP={100}>
        {!fileData ? (
          <div className="onboarding-file-viewer-error">
            <p>File data is not available</p>
          </div>
        ) : error ? (
          <div className="onboarding-file-viewer-error">
            <p>{error}</p>
          </div>
        ) : isPdfFile ? (
          blobUrl ? (
            <Worker workerUrl={'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'}>
              <Viewer fileUrl={blobUrl} />
            </Worker>
          ) : (
            <div className="onboarding-file-viewer-error">
              <p>Loading PDF...</p>
            </div>
          )
        ) : (
          blobUrl ? (
            <img 
              src={blobUrl} 
              alt={fileName || 'Document'} 
              className="onboarding-file-viewer-image" 
            />
          ) : (
            <div className="onboarding-file-viewer-error">
              <p>Loading image...</p>
            </div>
          )
        )}
      </Container>
    </Modal>
  );
};

export default OnboardingFileViewer;

OnboardingFileViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  fileData: PropTypes.object,
  mimeType: PropTypes.string,
  fileName: PropTypes.string,
};
